import { FC, useEffect, useState } from 'react';
import useSWR from 'swr';

import { mapResponseToSwimlaneAssetItem } from '@rikstv/play-common/src/forces/assetDetails/swimlaneItemMapper';
import { Channel } from '@rikstv/play-common/src/forces/channels/channel.types';
import { getRequestWithAccessToken } from '@rikstv/play-common/src/forces/utils/request';
import { useAuthToken } from '@rikstv/play-common/src/hooks/useAuthToken';
import { useIsAuthenticated } from '@rikstv/play-common/src/hooks/useIsAuthenticated';
import { captureExceptionInSentry } from '@rikstv/play-common/src/utils/errorTracker/tracking';
import { addParameters } from '@rikstv/play-common/src/utils/url/url.utils';
import { Loader, SecondaryButton } from '@rikstv/shared-components';

import { ButtonGroup } from '../../button/ButtonGroup';
import { FeaturedItem } from '../../swimlane/styles/FeaturedItem';
import { CoverPageGrid } from '../grid/CoverPageGrid';

import styles from './PreviouslyAiredGrid.module.css';
interface Props extends Pick<Channel, 'name'> {
  archiveUrl?: string;
}

export const PreviouslyAiredGrid: FC<Props> = ({ name, archiveUrl }) => {
  const [maxPrograms, setMaxPrograms] = useState(5);
  const { data: archivePrograms, isLoading } = useFetchChannelArchive(archiveUrl);

  const showMoreArchivePrograms = () => setMaxPrograms(prev => prev + 5);

  if (isLoading) {
    return <Loader size="large" />;
  }

  if (!archivePrograms || archivePrograms.length === 0) {
    return null;
  }

  return (
    <>
      <CoverPageGrid title={`Tidligere sendt på ${name}`} className={styles.previouslyAiredGrid}>
        {archivePrograms.slice(0, maxPrograms).map(asset => {
          return (
            <FeaturedItem
              asset={asset}
              noMargin
              showDescription
              key={'previously-aired-' + asset.links.details.id}
              swimlaneType={'Default'}
            />
          );
        })}
      </CoverPageGrid>
      {archivePrograms.length > maxPrograms && (
        <ButtonGroup>
          <SecondaryButton onClick={showMoreArchivePrograms}>Vis flere</SecondaryButton>
        </ButtonGroup>
      )}
    </>
  );
};

const useFetchChannelArchive = (archiveUrl?: string) => {
  const loggedIn = useIsAuthenticated();
  const accessToken = useAuthToken();
  const urlWithParams = archiveUrl ? addParameters(archiveUrl, { take: '20' }) : null;

  const swrResponse = useSWR(
    urlWithParams,
    url => {
      return getRequestWithAccessToken<RiksTV.ClientAPI.AssetSwimlaneResponse>(accessToken)(url).then(res =>
        mapResponseToSwimlaneAssetItem(res, {
          loggedIn,
          style: 'Featured',
          type: 'Default',
        })
      );
    },
    {
      // refresh on focus change if more than 5 minutes on idle
      focusThrottleInterval: 300_000,
      // autorefresh every 30 min
      refreshInterval: 1_800_000,
    }
  );

  useEffect(() => {
    if (swrResponse.error) {
      captureExceptionInSentry(swrResponse.error);
    }
  }, [swrResponse.error]);

  return swrResponse;
};
